import { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';

const formatKES = (amount) =>
    'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

export default function BoughtTogetherModal({ items, onAddToCart, onClose, onGoToCart }) {
    const [adding, setAdding] = useState({});
    const [added, setAdded] = useState({});

    if (!items || items.length === 0) return null;

    const handleAdd = async (item) => {
        if (adding[item.variantId] || added[item.variantId]) return;
        setAdding(prev => ({ ...prev, [item.variantId]: true }));
        try {
            await onAddToCart(item.variantId, 1);
            setAdded(prev => ({ ...prev, [item.variantId]: true }));
        } finally {
            setAdding(prev => ({ ...prev, [item.variantId]: false }));
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.45)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
                    <div>
                        <h2 className="text-base font-bold text-foreground">Frequently Bought Together</h2>
                        <p className="text-xs text-muted-foreground mt-0.5">Customers also buy these with this item</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground transition-colors"
                    >
                        <i className="ri-close-line text-lg"></i>
                    </button>
                </div>

                {/* Items list */}
                <div className="px-6 py-4 space-y-3 max-h-80 overflow-y-auto">
                    {items.map((item) => {
                        const imgSrc = item.image
                            ? (item.image.startsWith('http') ? item.image : `${API_URL}/${item.image}`)
                            : null;
                        const isAdded = added[item.variantId];
                        const isAdding = adding[item.variantId];

                        return (
                            <div key={item.variantId} className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/30 transition-colors">
                                {/* Image */}
                                <div className="w-14 h-14 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                                    {imgSrc ? (
                                        <img src={imgSrc} alt={item.name} className="w-full h-full object-contain p-1" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <i className="ri-image-line text-muted-foreground/40 text-xl"></i>
                                        </div>
                                    )}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/product/${item.slug}`}
                                        onClick={onClose}
                                        className="text-sm font-semibold text-foreground hover:text-primary transition-colors line-clamp-1"
                                    >
                                        {item.name}
                                    </Link>
                                    <p className="text-sm font-bold text-primary mt-0.5">{formatKES(item.price)}</p>
                                </div>

                                {/* Add button */}
                                <button
                                    onClick={() => handleAdd(item)}
                                    disabled={isAdding || isAdded}
                                    className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                        isAdded
                                            ? 'bg-green-100 text-green-700 cursor-default'
                                            : 'bg-primary text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed'
                                    }`}
                                >
                                    {isAdding ? (
                                        <span className="flex items-center gap-1">
                                            <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                                        </span>
                                    ) : isAdded ? (
                                        <span className="flex items-center gap-1"><i className="ri-check-line"></i> Added</span>
                                    ) : (
                                        <span className="flex items-center gap-1"><i className="ri-add-line"></i> Add</span>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="flex gap-3 px-6 pb-5 pt-3 border-t border-border">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 border border-border rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                    >
                        Continue Shopping
                    </button>
                    <button
                        onClick={onGoToCart}
                        className="flex-1 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:brightness-110 transition-all"
                    >
                        Go to Cart
                    </button>
                </div>
            </div>
        </div>
    );
}
